import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CCard, 
  CCardHeader,
  CCardBody, 
  CButton, 
  CBadge, 
  CSpinner 
} from '@coreui/react';
import api from '../../api/axiosInstance';

const QuestionBank = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  
  // 💡 Agar aapko test karna hai toh isko 1 ya 2 kar ke check kar sakte hain!
  const itemsPerPage = 9; 

  // Fallback Placeholder Image
  const defaultThumbnail = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='350' height='200' viewBox='0 0 350 200'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16px' fill='%2394a3b8'%3ENo Image Available%3C/text%3E%3C/svg%3E";
useEffect(() => {
  window.scrollTo(0, 0);
}, [section, title]);
  // Fetch Course Questions Data with Pagination
  useEffect(() => {
    const fetchCourseContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Dynamic API request with pagination parameters
        const res = await api.get(`/api/Admin/GetCourseContent?section=${section || 'Alpha'}&title=${title || 'SchoolLevel'}&page=${currentPage}&pageSize=${itemsPerPage}`);
        
        setCourseData(res.data);

        if (res.data) {
          const totalCount = res.data.totalQuestions || (res.data.questions ? res.data.questions.length : 0);
          const calculatedPages = res.data.totalPages || Math.ceil(totalCount / itemsPerPage) || 1;
          
          setTotalQuestions(totalCount);
          setTotalPages(calculatedPages);
        }
      } catch (err) {
        console.error("Error fetching question bank data:", err);
        setError("Failed to load question bank details.");
      } finally {
        setLoading(false);
      }
    };

    fetchCourseContent();
  }, [section, title, currentPage]);

  // Reset to Page 1 on route change
  useEffect(() => {
    setCurrentPage(1);
  }, [section, title]);

  // Image URL Fixer
  const getImageUrl = (thumbUrl) => {
    if (!thumbUrl) return defaultThumbnail;
    return thumbUrl;
  };

  // Navigate to View Question Solution
  const handleGetSolution = (questionId) => {
    navigate(`/content/view-question/${questionId}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Question Bank...</p>
      </div>
    );
  }

  if (error || !courseData) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error || "Data loading failed"}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate('/')}>Back to Home</CButton>
      </CContainer>
    );
  }

  const currentQuestions = courseData.questions || [];

  return (
    <CContainer className="py-4">
      {/* Header Banner */}
      <div className="mb-4 p-4 rounded-4 bg-dark text-white shadow-sm d-flex flex-column flex-md-row justify-content-between align-items-center border-start border-danger border-5">
        <div>
          <span className="text-danger fw-bold text-uppercase" style={{ fontSize: '12px', letterSpacing: '1px' }}>
            {courseData.section || 'School Level'} Question Repository
          </span>
          <h3 className="fw-bold m-0 text-white mt-1">{courseData.title || 'Question Bank'}</h3>
          <p className="text-muted m-0 small mt-1">{courseData.description}</p>
        </div>
        <CBadge color="danger" className="mt-2 mt-md-0 px-3 py-2 fs-6 rounded-pill">
          Total Questions: {totalQuestions}
        </CBadge>
      </div>

      {/* 3x3 Grid Layout */}
      {currentQuestions.length > 0 ? (
        <CRow className="g-4">
          {currentQuestions.map((q, idx) => {
            // Dynamic descending question index calculation
            const actualQIndex = totalQuestions - ((currentPage - 1) * itemsPerPage + idx);

            return (
              <CCol lg={4} md={6} sm={12} key={q.id}>
                <CCard className="h-100 border-0 shadow-sm rounded-4 overflow-hidden custom-card-hover" style={{ transition: 'all 0.3s ease' }}>
                  
                  {/* Card Header */}
                  <CCardHeader className="bg-light px-3 py-2.5 border-bottom d-flex align-items-center justify-content-between">
                    <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                      📌 Question No: {actualQIndex}
                    </span>
                    <CBadge color="info" className="text-dark" style={{ fontSize: '10px' }}>
                      {q.customQuestionId || `Ref #${q.id}`}
                    </CBadge>
                  </CCardHeader>

                  {/* Dynamic Thumbnail Image */}
                  <CCardBody className="p-2 bg-white d-flex align-items-center justify-content-center">
                    <div className="w-100 rounded-3 overflow-hidden border position-relative" style={{ height: '170px', backgroundColor: '#fdfdfd' }}>
                      <img 
                        src={getImageUrl(q.thumbnail)} 
                        alt={q.customQuestionId || `Question ${q.id}`} 
                        className="w-100 h-100 object-fit-cover hover-zoom"
                        style={{ transition: 'transform 0.3s ease' }}
                        onError={(e) => {
                          e.currentTarget.onerror = null; 
                          e.currentTarget.src = defaultThumbnail;
                        }}
                      />
                    </div>
                  </CCardBody>

                  {/* Get Solution Action Button */}
                  <div className="p-3 pt-0">
                    <CButton 
                      color="danger" 
                      className="w-100 fw-bold text-white rounded-3 py-2 shadow-sm d-flex align-items-center justify-content-center gap-2"
                      style={{ background: 'linear-gradient(135deg, #e63946, #c1121f)', border: 'none' }}
                      onClick={() => handleGetSolution(q.id)}
                    >
                      <span>👁️ Get Solution</span>
                    </CButton>
                  </div>
                </CCard>
              </CCol>
            );
          })}
        </CRow>
      ) : (
        <div className="alert alert-warning text-center">No questions available in this category.</div>
      )}

      {/* 🚀 Navigation Bar - Always visible as long as there is data */}
      {totalQuestions > 0 && (
        <div className="mt-5 p-3 bg-white rounded-4 shadow-sm border d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="fw-bold text-secondary" style={{ fontSize: '0.9rem' }}>
            Page <span className="text-dark">{currentPage}</span> of {totalPages}
          </div>

          <div className="d-flex align-items-center gap-1 flex-wrap">
            <button 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="btn btn-sm btn-outline-secondary rounded-pill px-3 me-2"
              style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              &laquo; Prev
            </button>

            {/* Render Page Buttons */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`btn btn-sm rounded-circle px-3 py-1 fw-bold ${currentPage === page ? 'btn-danger text-white shadow-sm' : 'btn-light text-dark'}`}
                style={{ cursor: 'pointer', minWidth: '35px' }}
              >
                {page}
              </button>
            ))}

            <button 
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="btn btn-sm btn-outline-secondary rounded-pill px-3 ms-2"
              style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next &raquo;
            </button>
          </div>
        </div>
      )}

      <style>{`
        .custom-card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
        }
        .custom-card-hover:hover .hover-zoom {
          transform: scale(1.04);
        }
      `}</style>
    </CContainer>
  );
};

export default QuestionBank;